import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useNotificationsStore from "../../store/useNotificationsStore";
import NotificationItem from "./NotificationItem";
import { notificationLink } from "./notificationHelpers";
import "./NotificationBell.css";

const PREVIEW_COUNT = 8;

// Fires once per real page load (login / refresh); it is NOT reset on client-side
// remounts, so the "you have unread notifications" login flash happens once at
// login and never on tab / workspace switches.
let loginFlashDone = false;

// Header bell + unread badge + dropdown preview of recent notifications.
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const items = useNotificationsStore((s) => s.items);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const pulse = useNotificationsStore((s) => s.pulse);
  const [flashing, setFlashing] = useState(false);
  const prevPulseRef = useRef(pulse);
  const loading = useNotificationsStore((s) => s.loading);
  const loadAll = useNotificationsStore((s) => s.loadAll);
  const refreshUnreadCount = useNotificationsStore((s) => s.refreshUnreadCount);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  // Flash for ~3s ONLY when a new notification actually arrives — i.e. when
  // `pulse` INCREASES while this component is mounted. Comparing against the
  // previous value prevents a stray flash when the bell remounts on tab /
  // workspace switches (pulse is already > 0 there, but hasn't changed).
  useEffect(() => {
    if (pulse > prevPulseRef.current) {
      prevPulseRef.current = pulse;
      setFlashing(true);
      const t = setTimeout(() => setFlashing(false), 3000);
      return () => clearTimeout(t);
    }
    prevPulseRef.current = pulse;
    return undefined;
  }, [pulse]);

  // One-time flash on login (fresh page load) when there are unread
  // notifications. The module-level flag makes it fire once per page load, so it
  // does NOT re-fire on tab / workspace switches.
  useEffect(() => {
    if (!loginFlashDone && unreadCount > 0) {
      loginFlashDone = true;
      setFlashing(true);
      const t = setTimeout(() => setFlashing(false), 3000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [unreadCount]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      refreshUnreadCount();
      // Always refresh the FULL list on open so live-pushed items never hide the
      // older ones. Silent (no loading flash) when we already have items.
      loadAll(items.length > 0);
    }
  };

  const activate = (n) => {
    if (!n.is_read) markRead(n.id);
    const link = notificationLink(n);
    setOpen(false);
    if (link) navigate(link);
  };

  const preview = items.slice(0, PREVIEW_COUNT);
  const badge = unreadCount > 9 ? "9+" : unreadCount;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={toggle}
        style={{ height: "36px", padding: "7px 9px", gap: "6px" }}
        className={`relative flex justify-center items-center rounded-md border border-[#DEDEDE] bg-white hover:bg-[#FAFAFA] transition-colors ${
          flashing ? "notif-bell-glow" : ""
        }`}
        aria-label="Notifications"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="20"
          viewBox="0 0 15 19"
          fill="none"
          className={flashing ? "notif-bell-swing" : ""}
        >
          <path
            d="M0.916667 15.5833C0.656944 15.5833 0.439236 15.4955 0.263542 15.3198C0.0878472 15.1441 0 14.9264 0 14.6667C0 14.4069 0.0878472 14.1892 0.263542 14.0135C0.439236 13.8378 0.656944 13.75 0.916667 13.75H1.83333V7.33333C1.83333 6.06528 2.21528 4.93854 2.97917 3.95312C3.74306 2.96771 4.73611 2.32222 5.95833 2.01667V1.375C5.95833 0.993056 6.09201 0.668403 6.35938 0.401042C6.62674 0.133681 6.95139 0 7.33333 0C7.71528 0 8.03993 0.133681 8.30729 0.401042C8.57465 0.668403 8.70833 0.993056 8.70833 1.375V2.01667C9.93056 2.32222 10.9236 2.96771 11.6875 3.95312C12.4514 4.93854 12.8333 6.06528 12.8333 7.33333V13.75H13.75C14.0097 13.75 14.2274 13.8378 14.4031 14.0135C14.5788 14.1892 14.6667 14.4069 14.6667 14.6667C14.6667 14.9264 14.5788 15.1441 14.4031 15.3198C14.2274 15.4955 14.0097 15.5833 13.75 15.5833H0.916667ZM7.33333 18.3333C6.82917 18.3333 6.39757 18.1538 6.03854 17.7948C5.67951 17.4358 5.5 17.0042 5.5 16.5H9.16667C9.16667 17.0042 8.98715 17.4358 8.62813 17.7948C8.2691 18.1538 7.8375 18.3333 7.33333 18.3333ZM3.66667 13.75H11V7.33333C11 6.325 10.641 5.46181 9.92292 4.74375C9.20486 4.02569 8.34167 3.66667 7.33333 3.66667C6.325 3.66667 5.46181 4.02569 4.74375 4.74375C4.02569 5.46181 3.66667 6.325 3.66667 7.33333V13.75Z"
            fill="#A04A97"
          />
        </svg>
        {unreadCount > 0 && (
          <span
            className={`absolute -top-2 -right-2 min-w-[20px] h-[20px] px-1 bg-[#A04A97] text-white text-[11px] font-semibold rounded-full flex items-center justify-center ${
              flashing ? "notif-badge-pop" : ""
            }`}
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-w-[92vw] bg-white rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 z-[10000] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#0000000D]">
            <span className="font-outfit text-[16px] font-semibold text-[#27272A]">
              Notifications
            </span>
            <button
              type="button"
              onClick={() => markAllRead()}
              disabled={unreadCount === 0}
              className={`text-[13px] font-medium ${
                unreadCount === 0
                  ? "text-[#C4C4C4] cursor-default"
                  : "text-[#A04A97] hover:underline"
              }`}
            >
              Mark all as read
            </button>
          </div>

          {/* List — caps at 400px tall, then scrolls */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading && preview.length === 0 ? (
              <div className="px-4 py-10 text-center text-[13px] text-[#A1A1AA]">
                Loading…
              </div>
            ) : preview.length === 0 ? (
              <div className="px-4 py-10 flex flex-col items-center justify-center text-center">
                <img
                  src="/no-notifications.svg"
                  alt="No notifications"
                  className="w-40 max-w-[70%] h-auto mb-3"
                />
                <p className="font-outfit text-[14px] font-medium text-[#52525B]">
                  You currently have no new notifications!
                </p>
              </div>
            ) : (
              preview.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  compact
                  onActivate={activate}
                />
              ))
            )}
          </div>

          {/* Footer */}
          <button
            type="button"
            className="w-full px-4 py-3 border-t border-[#0000000D] text-[13px] font-medium text-[#A04A97] hover:bg-[#FAFAFA]"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
